import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import BottomNav from '../components/BottomNav';
import InstallBanner from '../components/InstallBanner';
import InstallAppAction from '../components/InstallAppAction';
import OnboardingTooltip from '../components/OnboardingTooltip';
import NotificationBell from '../components/NotificationBell';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  Droplets, MessageSquare, ShoppingBag, TrendingUp, 
  Award, ChevronRight, Fuel, User, LogOut, Settings, Smartphone,
  Home, Package, CreditCard, Gift, MapPin, Clock, CheckCircle, XCircle
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Onboarding steps for clients
const clientOnboardingSteps = [
  {
    icon: Home,
    title: "Bem-vindo ao HáLubri!",
    message: "A sua plataforma digital para comprar lubrificantes Sonangol de forma rápida e conveniente.",
    tips: "Esta é a sua página inicial onde pode ver produtos em destaque e aceder a todas as funcionalidades.",
    bgColor: "bg-gradient-to-r from-sonangol-red to-sonangol-orange"
  },
  {
    icon: MessageSquare,
    title: "Assistente IA",
    message: "Não sabe qual óleo escolher? O nosso assistente inteligente ajuda-o a encontrar o lubrificante ideal para o seu veículo.",
    tips: "Descreva o seu carro (ex: 'Toyota Hilux 2018') e receba uma recomendação personalizada!",
    bgColor: "bg-gradient-to-r from-blue-600 to-blue-500"
  },
  {
    icon: Package,
    title: "Catálogo de Produtos",
    message: "Explore a nossa gama completa de lubrificantes Ngol. Filtre por categoria e veja os detalhes de cada produto.",
    tips: "Toque num produto para ver especificações técnicas e adicionar ao cesto.",
    bgColor: "bg-gradient-to-r from-green-600 to-green-500"
  },
  {
    icon: CreditCard,
    title: "Checkout Fácil",
    message: "Escolha entre retirar na bomba ou receber em casa. Pague com Multicaixa Express, Mobile Money ou dinheiro.",
    tips: "Clientes corporate têm entrega exclusiva no endereço registado.",
    bgColor: "bg-gradient-to-r from-purple-600 to-purple-500"
  },
  {
    icon: Gift,
    title: "Cartão Fidelidade",
    message: "Acumule pontos em cada compra! 1 ponto por cada 1.000 AOA gastos. Troque por descontos e ofertas exclusivas.",
    tips: "Aceda ao seu cartão de fidelidade no menu inferior.",
    bgColor: "bg-gradient-to-r from-amber-500 to-orange-500"
  }
];

const HomePage = () => {
  const navigate = useNavigate();
  const { user, api, logout } = useAuth();
  const { fetchCart, cartCount } = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fuelStations, setFuelStations] = useState([]);
  const [loadingFuel, setLoadingFuel] = useState(true);

  const handleLogout = () => {
    logout();
    navigate('/auth');
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        // Seed database if empty
        await api().post('/seed');
        const res = await api().get('/products');
        setProducts(res.data.slice(0, 4));
        fetchCart();
      } catch (err) {
        console.error('Error loading data:', err);
      }
      setLoading(false);
    };
    loadData();
  }, [api, fetchCart]);

  // Load fuel stations (Sonangol DC+)
  useEffect(() => {
    const loadFuelStations = async () => {
      try {
        const res = await axios.get(`${API}/api/fuel/stations`);
        // Sort by stations that have fuel first, then by update recency
        const sorted = res.data.sort((a, b) => {
          const aHasFuel = a.has_gasoleo || a.has_gasolina;
          const bHasFuel = b.has_gasoleo || b.has_gasolina;
          if (aHasFuel && !bHasFuel) return -1;
          if (!aHasFuel && bHasFuel) return 1;
          return 0;
        });
        setFuelStations(sorted.slice(0, 4));
      } catch (err) {
        console.error('Error loading fuel stations:', err);
      }
      setLoadingFuel(false);
    };
    loadFuelStations();
  }, []);

  const quickActions = [
    { 
      icon: MessageSquare, 
      label: 'Assistente IA', 
      description: 'Encontrar o óleo ideal',
      path: '/assistant',
      color: 'bg-gradient-to-br from-sonangol-red to-red-600'
    },
    { 
      icon: ShoppingBag, 
      label: 'Catálogo', 
      description: 'Ver todos os produtos',
      path: '/catalog',
      color: 'bg-gradient-to-br from-sonangol-orange to-orange-600'
    },
  ];

  return (
    <div className="min-h-screen bg-zinc-100 pb-20">
      {/* Onboarding Tooltips for first-time users */}
      <OnboardingTooltip 
        storageKey="halubri_client_onboarding_seen"
        steps={clientOnboardingSteps}
      />

      {/* Header */}
      <div className="bg-gradient-to-br from-sonangol-red to-sonangol-orange pt-4 pb-8 px-4 rounded-b-3xl">
        {/* Sonangol Logo */}
        <div className="flex justify-center mb-4">
          <img 
            src="https://customer-assets.emergentagent.com/job_d7626c81-a248-46cd-9b36-40ecfdffbad5/artifacts/2ubuie86_logo_sonangol_undp.png"
            alt="Sonangol Distribuição e Comercialização"
            className="h-10 object-contain"
            data-testid="sonangol-logo-home"
          />
        </div>
        <div className="flex items-center justify-between mb-6">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-3 hover:opacity-90 transition-opacity" data-testid="user-menu-btn">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                  <User className="w-5 h-5 text-white" />
                </div>
                <div className="text-left">
                  <p className="text-white/80 text-sm font-manrope">Olá,</p>
                  <h1 className="text-white font-barlow text-xl font-bold">{user?.name?.split(' ')[0]}</h1>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              <div className="px-2 py-2">
                <p className="font-barlow font-bold text-zinc-800">{user?.name}</p>
                <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/loyalty" className="flex items-center gap-2 cursor-pointer">
                  <Award className="w-4 h-4" />
                  <span>Cartão de Fidelidade</span>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/orders" className="flex items-center gap-2 cursor-pointer">
                  <ShoppingBag className="w-4 h-4" />
                  <span>Meus Pedidos</span>
                </Link>
              </DropdownMenuItem>
              <InstallAppAction>
                {(install) => (
                  <DropdownMenuItem onClick={install} className="flex items-center gap-2 cursor-pointer" data-testid="menu-install-app">
                    <Smartphone className="w-4 h-4" />
                    <span>Instalar App</span>
                  </DropdownMenuItem>
                )}
              </InstallAppAction>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={handleLogout}
                className="text-red-600 focus:text-red-600 cursor-pointer"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                <span>Terminar Sessão</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <Link 
              to="/cart" 
              className="relative w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center"
              data-testid="cart-btn"
            >
              <ShoppingBag className="w-5 h-5 text-white" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-white text-sonangol-red text-xs font-bold rounded-full flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </Link>
          </div>
        </div>

        {/* Loyalty card preview */}
        <Link to="/loyalty" data-testid="loyalty-card-link">
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                  <Award className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-white/80 text-xs">Cartão de Fidelidade</p>
                  <p className="text-white font-barlow font-bold">{user?.loyalty_points || 0} pontos</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-white/60" />
            </div>
          </div>
        </Link>
      </div>

      {/* Main Content */}
      <div className="px-4 -mt-4 space-y-6">
        <InstallBanner />

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-3">
          {quickActions.map((action, idx) => (
            <Link key={idx} to={action.path} data-testid={`quick-action-${idx}`}>
              <div className={`${action.color} rounded-2xl p-4 text-white active-scale shadow-lg`}>
                <action.icon className="w-8 h-8 mb-3" />
                <h3 className="font-barlow font-bold text-lg">{action.label}</h3>
                <p className="text-white/80 text-xs font-manrope">{action.description}</p>
              </div>
            </Link>
          ))}
        </div>

        {/* Featured Products */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-barlow font-bold text-xl text-zinc-800">Produtos em Destaque</h2>
            <Link to="/catalog" className="tap-target text-sonangol-red text-sm font-medium" data-testid="see-all-products">
              Ver todos
            </Link>
          </div>

          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-white rounded-xl h-24 animate-pulse"></div>
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {products.map((product) => (
                <Link key={product.id} to={`/product/${product.id}`} data-testid={`product-card-${product.id}`}>
                  <div className="bg-white rounded-xl p-4 flex gap-4 items-center active-scale shadow-sm border border-zinc-100">
                    <div className="w-16 h-16 bg-zinc-100 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {product.image_url ? (
                        <img 
                          src={product.image_url} 
                          alt={product.name}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                        />
                      ) : null}
                      <Droplets className={`w-8 h-8 text-sonangol-red ${product.image_url ? 'hidden' : ''}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-barlow font-bold text-zinc-800 truncate">{product.name}</h3>
                      <p className="text-xs text-zinc-500 font-manrope">
                        {product.viscosity} · {product.volume_liters}L
                      </p>
                      <p className="font-barlow font-bold text-sonangol-red mt-1">
                        {product.price_aoa.toLocaleString('pt-AO')} AOA
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-zinc-400" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Sonangol DC+ - Fuel Availability */}
        <div className="bg-gradient-to-r from-zinc-900 to-zinc-800 rounded-2xl p-5 relative overflow-hidden">
          <div className="absolute right-0 top-0 w-32 h-32 bg-sonangol-red/20 rounded-full blur-3xl"></div>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-sonangol-orange rounded-xl flex items-center justify-center">
              <Fuel className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-barlow font-bold text-xl text-white">
                Sonangol DC+
              </h3>
              <p className="text-zinc-400 text-xs font-manrope">
                Verifique a disponibilidade de combustível
              </p>
            </div>
          </div>
          
          {loadingFuel ? (
            <div className="space-y-2">
              {[1, 2].map(i => (
                <div key={i} className="bg-white/5 rounded-xl h-16 animate-pulse"></div>
              ))}
            </div>
          ) : fuelStations.length === 0 ? (
            <p className="text-zinc-500 text-sm text-center py-4">
              Nenhuma informação de combustível disponível
            </p>
          ) : (
            <div className="space-y-2">
              {fuelStations.map((station) => {
                const hasGasoleo = station.has_gasoleo;
                const hasGasolina = station.has_gasolina;
                const hasFuel = hasGasoleo || hasGasolina;
                
                return (
                  <div 
                    key={station.id}
                    className={`bg-white/5 backdrop-blur-sm rounded-xl p-3 border ${hasFuel ? 'border-green-500/30' : 'border-white/10'}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium text-sm truncate">{station.name}</p>
                        <div className="flex items-center gap-1 text-zinc-500 text-xs mt-0.5">
                          <MapPin className="w-3 h-3" />
                          <span className="truncate">{station.city || station.address}</span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {/* Gasóleo Badge */}
                        <div className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${
                          hasGasoleo ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                        }`}>
                          {hasGasoleo ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                          Gasóleo
                        </div>
                        {/* Gasolina Badge */}
                        <div className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${
                          hasGasolina ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                        }`}>
                          {hasGasolina ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                          Gasolina
                        </div>
                      </div>
                    </div>
                    {station.fuel_updated_at && (
                      <div className="flex items-center gap-1 text-zinc-600 text-xs mt-2">
                        <Clock className="w-3 h-3" />
                        Actualizado: {new Date(station.fuel_updated_at).toLocaleString('pt-AO', {
                          day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          
          <Link to="/fuel-stations" className="block mt-4">
            <Button 
              variant="outline" 
              className="w-full bg-white/10 border-white/20 text-white hover:bg-white/20"
              data-testid="view-all-fuel-btn"
            >
              <MapPin className="w-4 h-4 mr-2" />
              Ver Todas as Estações
            </Button>
          </Link>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default HomePage;
